import React, { FunctionComponent, useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import i18next from 'i18next';

import useTypedSelector from 'hooks/useTypedSelector';
import { onDelete, onGetAll } from 'redux/actions/blogs.actions';
import { blogsSelector } from 'redux/selectors/blogs.selector';
import { history } from 'helpers/history';
import { COLORS } from 'helpers/enums/colors';

import LayoutContainer from 'containers/LayoutContainer';
import BlogsView from 'views/BlogsView';
import ActionButtons from 'components/commons/Table/components/ActionButtons';
import Dialog from 'components/commons/Dialog';

import { IBlog } from 'types/blogs.types';
import { ITableUpdate } from 'components/commons/Table/types';

import 'views/BlogsView/i18n';

const BlogsContainer: FunctionComponent = () => {
  const dispatch = useDispatch();
  const [deleteItem, setDeleteItem] = useState<IBlog | undefined>();
  const { count, loading, table } = useTypedSelector((state) => state.blogs);
  const items = useTypedSelector(blogsSelector);

  const actions = [
    {
      label: i18next.t('BLOGS_VIEW:EDIT'),
      onClick: (id: string) => history.push(`/blogs/${id}/edit`),
    },
    {
      label: i18next.t('BLOGS_VIEW:DELETE'),
      onClick: (id: string) => setDeleteItem(items.find((item: IBlog) => item.id === Number(id))),
      color: COLORS.redMonza,
    },
  ];

  const columns = [
    {
      name: 'title',
      label: i18next.t('BLOGS_VIEW:NAME'),
    },
    {
      name: 'createdAt',
      label: i18next.t('BLOGS_VIEW:DATE'),
    },
    {
      name: 'id',
      label: i18next.t('BLOGS_VIEW:ACTIONS'),
      options: {
        customBodyRender: (value: string) => <ActionButtons id={value} items={actions} />,
        sort: false,
      },
    },
  ];

  const onTableUpdate = useCallback((data: ITableUpdate) => dispatch(onGetAll(data)), [dispatch]);

  const onDeleteClick = (isConfirmed = false) => {
    if (isConfirmed && deleteItem) dispatch(onDelete(deleteItem.id.toString()));
    setDeleteItem(undefined);
  };

  return (
    <LayoutContainer auth title={i18next.t<string>('BLOGS_VIEW:TITLE')}>
      <BlogsView
        columns={columns}
        count={count}
        data={items}
        loading={loading}
        onAddClick={() => history.push('/blogs/create')}
        onTableUpdate={onTableUpdate}
        table={table}
      />
      <Dialog
        message={i18next.t('BLOGS_VIEW:DELETE_DIALOG_MESSAGE', {
          name: `${deleteItem?.title}`,
        })}
        onCancelClick={() => onDeleteClick()}
        onConfirmClick={() => onDeleteClick(true)}
        open={!!deleteItem}
        title={i18next.t('BLOGS_VIEW:DELETE_DIALOG_TITLE')}
      />
    </LayoutContainer>
  );
};

export default BlogsContainer;
